<template>
  <div
    class="integration-card"
    :class="{
      'integration-card--installed': isInstalled,
      'integration-card--pending': shopIntegrationStatus === 'pending_auth',
      'integration-card--soon': isComingSoon,
    }"
  >
    <!-- Card Top -->
    <div class="card-top">
      <div
        class="card-icon"
        :class="{ 'has-image': integration.icon_url }"
      >
        <img
          v-if="integration.icon_url"
          :src="integration.icon_url"
          :alt="localizedName"
        >
        <i
          v-else
          class="fas fa-puzzle-piece"
        />
      </div>

      <div class="card-meta">
        <h3 class="card-name">
          {{ localizedName }}
        </h3>
        <p
          v-if="partnerName"
          class="card-partner"
        >
          {{ partnerName }}
        </p>
        <span
          v-if="isRestricted"
          class="restricted-badge"
          :title="t('integrations.visibility.restrictedHint')"
        >
          <i class="fas fa-lock" />
          {{ t('integrations.visibility.restricted') }}
        </span>
      </div>

      <!-- Status indicator -->
      <span
        v-if="isInstalled && shopIntegrationStatus !== 'pending_auth'"
        class="installed-badge"
      >
        <i class="fas fa-check" />
        {{ t('integrations.status.installed') }}
      </span>
      <span
        v-else-if="shopIntegrationStatus === 'pending_auth'"
        class="pending-badge"
      >
        <i class="fas fa-clock" />
        {{ t('integrations.status.pendingAuth') }}
      </span>
      <span
        v-else-if="isComingSoon"
        class="soon-badge"
      >
        <i class="fas fa-hourglass-half" />
        {{ t('integrations.status.soon') }}
      </span>
    </div>

    <!-- Description -->
    <p class="card-description">
      {{ localizedShortDescription }}
    </p>

    <!-- Categories -->
    <div
      v-if="integration.integration_categories_data && integration.integration_categories_data.length > 0"
      class="card-categories"
    >
      <span
        v-for="(category, index) in integration.integration_categories_data"
        :key="category.id_category || index"
        class="category-chip"
      >
        {{ getCategoryName(category) }}
      </span>
    </div>

    <!-- Spacer to push footer down -->
    <div class="card-spacer" />

    <!-- Card Footer -->
    <div class="card-footer">
      <span
        v-if="integration.version"
        class="card-version"
      >
        v{{ integration.version }}
      </span>

      <div class="card-actions">
        <!-- Pending auth: cancel only -->
        <template v-if="shopIntegrationStatus === 'pending_auth'">
          <button
            class="btn btn--outline-danger"
            @click="$emit('cancel-installation', integration)"
          >
            <i class="fas fa-times" />
            {{ t('integrations.actions.cancelInstallation') }}
          </button>
        </template>

        <!-- Already installed: view details only -->
        <template v-else-if="isInstalled">
          <button
            class="btn btn--secondary"
            @click="$emit('view-details', integration)"
          >
            <i class="fas fa-info-circle" />
            {{ t('integrations.actions.viewDetails') }}
          </button>
        </template>

        <!-- Not installed: details + install (or soon: details + disabled CTA) -->
        <template v-else>
          <button
            class="btn btn--ghost"
            @click="$emit('view-details', integration)"
          >
            {{ t('integrations.actions.viewDetails') }}
          </button>
          <button
            v-if="isComingSoon"
            type="button"
            class="btn btn--soon"
            disabled
          >
            <i class="fas fa-hourglass-half" />
            {{ t('integrations.actions.comingSoon') }}
          </button>
          <button
            v-else
            class="btn btn--primary"
            @click="$emit('install', integration)"
          >
            <i class="fas fa-download" />
            {{ t('integrations.actions.install') }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import type { PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Integration } from '@/composables/useIntegrations';
import { pickCategoryName, pickLocalizedText } from '@/utils/localized-text';

interface CategoryData {
  id_category?: string | number;
  category_name: string;
}

export default defineComponent({
  name: 'IntegrationCard',

  props: {
    integration: {
      type: Object as PropType<Integration>,
      required: true,
    },
    isInstalled: {
      type: Boolean,
      default: false,
    },
    shopIntegrationStatus: {
      type: String,
      default: '',
    },
  },

  emits: ['install', 'cancel-installation', 'view-details'],

  setup(props) {
    const { t, locale } = useI18n();

    const partnerName = computed(() => {
      if (!props.integration.partners_data || props.integration.partners_data.length === 0) {
        return '';
      }
      return props.integration.partners_data[0].partner_name || '';
    });

    // Coming-soon integrations are visible in the marketplace but cannot be
    // installed yet. Surfaces a distinct badge + disabled CTA.
    const isComingSoon = computed(() => props.integration.status === 'soon');

    // Portée par boutique : intégration réservée par ShopiMind à une sélection de
    // boutiques. Signalée pour qu'une boutique autorisée sache qu'elle n'est pas publique.
    const isRestricted = computed(() => props.integration.visibility === 'restricted');

    // G14 — nom / description courte localisés (C10) avec repli legacy.
    const localizedName = computed(() => pickLocalizedText(
      (props.integration as any)?.name_i18n,
      locale.value,
    ) || props.integration.name || '');

    const localizedShortDescription = computed(() => pickLocalizedText(
      (props.integration as any)?.short_description_i18n,
      locale.value,
    ) || props.integration.short_description || '');

    // Parseur `category_name` + sélection localisée centralisés (G12).
    const getCategoryName = (category: CategoryData): string => pickCategoryName(category.category_name, locale.value);

    return {
      t,
      partnerName,
      isComingSoon,
      isRestricted,
      getCategoryName,
      localizedName,
      localizedShortDescription,
    };
  },
});
</script>

<style scoped lang="scss">
.integration-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  height: 100%;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-color: #d1d5db;
    transform: translateY(-1px);
  }

  &--installed {
    border-color: rgba(148, 200, 64, 0.25);
    background: #fcfef7;
  }

  &--pending {
    border-color: #fed7aa;
    background: #fffbf5;
  }

  &--soon {
    border-color: #c7d2fe;
    background: #f8faff;

    &:hover {
      border-color: #a5b4fc;
    }
  }
}

/* Card Top: icon + name + badge */
.card-top {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.card-icon {
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #94c840 0%, #7fb32e 100%);
  border-radius: 0.625rem;
  color: #ffffff;
  font-size: 1.25rem;
  overflow: hidden;

  &.has-image {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    padding: 0.125rem;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 0.5rem;
  }
}

.card-meta {
  flex: 1;
  min-width: 0;
}

.card-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.3;
}

.card-partner {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0.125rem 0 0;
}

.installed-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: rgba(148, 200, 64, 0.15);
  color: #4d7c0f;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;

  i {
    font-size: 0.5625rem;
  }
}

.pending-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #fef3c7;
  color: #b45309;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;

  i {
    font-size: 0.5625rem;
  }
}

.soon-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;

  i {
    font-size: 0.5625rem;
  }
}

/* Portée restreinte */
.restricted-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.375rem;
  padding: 0.125rem 0.5rem;
  background: #f3e8ff;
  color: #6b21a8;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;

  i {
    font-size: 0.5625rem;
  }
}

/* Description */
.card-description {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Categories */
.card-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.category-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.1875rem 0.5rem;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* Spacer */
.card-spacer {
  flex: 1;
}

/* Footer */
.card-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
  min-width: 0;
}

.card-version {
  font-family: monospace;
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 500;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-left: auto;
  min-width: 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border-radius: 7px;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  text-decoration: none;

  i {
    font-size: 0.75rem;
  }

  &--primary {
    background: #94c840;
    color: #ffffff;
    border-color: #94c840;

    &:hover {
      background: #7fb32e;
      border-color: #7fb32e;
    }
  }

  &--secondary {
    background: #ffffff;
    color: #374151;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  }

  &--ghost {
    background: transparent;
    color: #6b7280;
    border-color: transparent;

    &:hover {
      background: #f3f4f6;
      color: #374151;
    }
  }

  &--outline-danger {
    background: #ffffff;
    color: #dc2626;
    border-color: #fecaca;

    &:hover {
      background: #fef2f2;
      border-color: #dc2626;
    }
  }

  &--soon {
    background: #eef2ff;
    color: #4338ca;
    border-color: #c7d2fe;
    cursor: not-allowed;
    opacity: 0.85;

    &:hover {
      background: #eef2ff;
      border-color: #c7d2fe;
    }
  }
}
</style>
